import { useEffect, useRef, useState } from "react";
import API, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { PortalPage } from "../../components/portal/PortalPage";
import { usePortal, refreshPortalMe } from "../../components/portal/usePortal";
import { useBrandName } from "../../components/portal/PortalBrand";
import { fmtDateTime } from "../../components/portal/portalUtils";
import { PaperPlaneRight, ChatCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function ClientMessagesPage() {
  const { perms, client } = usePortal();
  const brandName = useBrandName();
  const [msgs, setMsgs] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  const load = () => API.get("/client-hub/messages").then(({ data }) => { setMsgs(data.messages || []); refreshPortalMe(); }).catch(() => setMsgs([]));
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs?.length]);

  const send = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try { await API.post("/client-hub/messages", { text }); setText(""); load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <PortalPage title="Messages" subtitle={`Direct line to the ${brandName} team looking after ${client?.display_name || "your account"}.`} testid="client-messages">
      <div className="border border-border rounded-lg bg-card flex flex-col h-[65vh]">
        <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="messages-thread">
          {msgs === null ? <p className="text-xs text-muted-foreground">Loading…</p> : msgs.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center"><ChatCircle size={36} className="text-muted-foreground/40 mb-2" weight="duotone" /><p className="text-sm font-semibold">No messages yet</p><p className="text-xs text-muted-foreground">Ask a question or share an update — we'll reply here.</p></div>
          ) : msgs.map((m) => {
            const mine = m.sender === "client";
            return (
              <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`message-${m.id}`}>
                <div className={`max-w-[75%] rounded-lg px-3 py-2 ${mine ? "bg-primary text-primary-foreground" : "bg-muted"}`}>
                  <p className="text-sm whitespace-pre-wrap">{m.text}</p>
                  <p className={`text-[10px] mt-1 ${mine ? "text-primary-foreground/70" : "text-muted-foreground"}`}>{m.sender_name || (mine ? "You" : brandName)} · {fmtDateTime(m.created_at)}</p>
                </div>
              </div>
            );
          })}
          <div ref={endRef} />
        </div>
        {perms.can_message ? (
          <div className="border-t border-border p-3 flex gap-2">
            <Textarea rows={2} value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} placeholder="Type a message… (Enter to send)" className="resize-none" data-testid="message-input" />
            <Button onClick={send} disabled={busy || !text.trim()} data-testid="message-send"><PaperPlaneRight size={16} /></Button>
          </div>
        ) : <p className="border-t border-border p-3 text-[11px] text-muted-foreground">Viewers can read messages but not send them.</p>}
      </div>
    </PortalPage>
  );
}
